import { useEffect, useMemo, useState } from "react";
import { approvePendingVisit, rejectPendingVisit } from "./api";
import { DashboardCustomizer } from "./DashboardCustomizer";
import { DashboardShell } from "./DashboardShell";
import { SiteSwitcher } from "./SiteSwitcher";
import { useSiteContext } from "./use-site-context";
import { formatDateTime } from "./format";
import { InviteVisitorForm } from "./InviteVisitorForm";
import { HostPassRequestForm } from "./HostPassRequestForm";
import type { VisitRow } from "./types";
import { useDashboardData } from "./use-dashboard-data";
import { useWidgetPrefs } from "./useWidgetPrefs";
import { EmptyRow } from "./ui";
import { VisitsSparkline } from "./VisitsSparkline";
import { EMPLOYEE_DEFAULT_WIDGETS, EMPLOYEE_WIDGETS } from "./widgets";
import { QrBadge } from "../../components/QrBadge";
import { useConfirm } from "../../components/useConfirm";
import { useToast } from "../../components/useToast";

type ApprovalAction = "approve" | "reject";

function getActionKey(action: ApprovalAction, visitId: number) {
  return `${action}-${visitId}`;
}

function ApprovalTable({
  rows,
  selectedVisitId,
  onSelect,
  onAction,
  pendingKey,
}: {
  rows: VisitRow[];
  selectedVisitId: number | null;
  onSelect: (visitId: number) => void;
  onAction: (action: ApprovalAction, visitId: number) => Promise<void>;
  pendingKey: string | null;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Visitor</th>
            <th>Company</th>
            <th>Purpose</th>
            <th>Expected Exit</th>
            <th>Requested</th>
            <th>Badge</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <EmptyRow label="No pending approvals" colSpan={7} />
          ) : (
            rows.map((visit) => {
              const approveKey = getActionKey("approve", visit.visit_id);
              const rejectKey = getActionKey("reject", visit.visit_id);
              const isBusy = pendingKey === approveKey || pendingKey === rejectKey;
              const isSelected = selectedVisitId === visit.visit_id;

              return (
                <tr
                  key={`employee-pending-${visit.visit_id}`}
                  className={isSelected ? "approval-table-row selected" : "approval-table-row"}
                  onClick={() => onSelect(visit.visit_id)}
                >
                  <td>{visit.visitor_name}</td>
                  <td>{visit.company || "-"}</td>
                  <td>{visit.purpose || "-"}</td>
                  <td>{formatDateTime(visit.expected_exit)}</td>
                  <td>{formatDateTime(visit.created_at)}</td>
                  <td>{visit.badge_code || "-"}</td>
                  <td className="action-cell">
                    <button
                      className="small-btn success-btn"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void onAction("approve", visit.visit_id);
                      }}
                      disabled={isBusy}
                    >
                      {pendingKey === approveKey ? "Approving..." : "Approve"}
                    </button>
                    <button
                      className="small-btn danger-btn"
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void onAction("reject", visit.visit_id);
                      }}
                      disabled={isBusy}
                    >
                      {pendingKey === rejectKey ? "Rejecting..." : "Reject"}
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export function EmployeeDashboardPage() {
  const siteContext = useSiteContext();
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const { data, loading, refreshing, error, lastUpdated, statsCards, refresh } =
    useDashboardData(selectedSiteId);
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();
  const { widgetIds, saving, save, resetToDefault } = useWidgetPrefs(EMPLOYEE_DEFAULT_WIDGETS);
  const [showCustomizer, setShowCustomizer] = useState(false);

  const pendingApprovals = useMemo(() => data?.pendingApprovals ?? [], [data]);
  const recentVisitors = useMemo(() => data?.recentVisitors ?? [], [data]);

  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [selectedVisitId, setSelectedVisitId] = useState<number | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (pendingApprovals.length === 0) {
      setSelectedVisitId(null);
      return;
    }

    const hasCurrentSelection = pendingApprovals.some(
      (visit) => visit.visit_id === selectedVisitId,
    );
    if (!hasCurrentSelection) {
      setSelectedVisitId(pendingApprovals[0].visit_id);
    }
  }, [pendingApprovals, selectedVisitId]);

  const selectedVisit = useMemo(
    () =>
      selectedVisitId
        ? pendingApprovals.find((visit) => visit.visit_id === selectedVisitId) ?? null
        : null,
    [pendingApprovals, selectedVisitId],
  );

  async function handleAction(action: ApprovalAction, visitId: number) {
    if (action === "reject") {
      const ok = await confirm({
        title: "Reject this visit?",
        message: `Visit #${visitId} will be marked REJECTED. This can't be undone from here.`,
        confirmLabel: "Reject",
        danger: true,
      });
      if (!ok) return;
    }

    const key = getActionKey(action, visitId);
    setPendingKey(key);
    setActionMessage(null);
    setActionError(null);

    try {
      const result =
        action === "approve"
          ? await approvePendingVisit(visitId)
          : await rejectPendingVisit(visitId);

      const message = `Visit ${result.visit_id} updated to ${result.status}`;
      setActionMessage(message);
      showSuccess(message);
      await refresh();
    } catch (requestError) {
      const message =
        requestError instanceof Error ? requestError.message : "Unable to update approval status";
      setActionError(message);
      showError(message);
    } finally {
      setPendingKey(null);
    }
  }

  return (
    <DashboardShell
      roleName="Host"
      title="Host Workspace"
      subtitle="Invite visitors, request long-term passes, and approve the requests assigned to you."
      loading={loading}
      refreshing={refreshing}
      error={error}
      lastUpdated={lastUpdated}
      onRefresh={refresh}
      siteSwitcher={
        <SiteSwitcher context={siteContext} value={selectedSiteId} onChange={setSelectedSiteId} />
      }
      showCustomizer={showCustomizer}
      onToggleCustomize={() => setShowCustomizer((prev) => !prev)}
      customizerPanel={
        <DashboardCustomizer
          catalog={EMPLOYEE_WIDGETS}
          value={widgetIds}
          saving={saving}
          onSave={async (ids) => {
            await save(ids);
            setShowCustomizer(false);
          }}
          onResetToDefault={async () => {
            await resetToDefault();
            setShowCustomizer(false);
          }}
          onClose={() => setShowCustomizer(false)}
        />
      }
    >
      <section className="stats-grid fade-in delay-1">
        {statsCards.map((card) => (
          <article className="stat-card" key={`employee-stat-${card.label}`}>
            <p className="stat-label">{card.label}</p>
            <p className="stat-value">{card.value}</p>
          </article>
        ))}
        <VisitsSparkline />
      </section>

      <section className="fade-in delay-2">
        <InviteVisitorForm />
      </section>

      <section className="fade-in delay-2">
        <HostPassRequestForm />
      </section>

      <section className="panel-grid-full fade-in delay-2">
        <article className="panel">
          <header className="panel-header">
            <h2>Pending Approvals</h2>
            <span>{pendingApprovals.length} rows</span>
          </header>
          <ApprovalTable
            rows={pendingApprovals}
            selectedVisitId={selectedVisitId}
            onSelect={setSelectedVisitId}
            onAction={handleAction}
            pendingKey={pendingKey}
          />
        </article>

        <article className="panel">
          <header className="panel-header">
            <h2>Approval Context</h2>
            <span>{selectedVisit ? `Visit #${selectedVisit.visit_id}` : "No selection"}</span>
          </header>
          {selectedVisit ? (
            <div className="approval-context">
              <div className="context-grid">
                <div className="context-item">
                  <p className="context-label">Visitor</p>
                  <p>{selectedVisit.visitor_name}</p>
                </div>
                <div className="context-item">
                  <p className="context-label">Company</p>
                  <p>{selectedVisit.company || "-"}</p>
                </div>
                <div className="context-item">
                  <p className="context-label">Host</p>
                  <p>{selectedVisit.host_name || "-"}</p>
                </div>
                <div className="context-item">
                  <p className="context-label">Purpose</p>
                  <p>{selectedVisit.purpose || "-"}</p>
                </div>
                <div className="context-item">
                  <p className="context-label">Expected Exit</p>
                  <p>{formatDateTime(selectedVisit.expected_exit)}</p>
                </div>
                <div className="context-item">
                  <p className="context-label">Requested At</p>
                  <p>{formatDateTime(selectedVisit.created_at)}</p>
                </div>
                <div className="context-item">
                  <p className="context-label">Badge</p>
                  <p>{selectedVisit.badge_code || "-"}</p>
                </div>
                <div className="context-item">
                  <p className="context-label">Status</p>
                  <p>{selectedVisit.status || "-"}</p>
                </div>
              </div>

              {selectedVisit.badge_code ? <QrBadge badgeCode={selectedVisit.badge_code} /> : null}

              <div className="context-actions">
                <button
                  className="small-btn success-btn"
                  type="button"
                  onClick={() => void handleAction("approve", selectedVisit.visit_id)}
                  disabled={pendingKey === getActionKey("approve", selectedVisit.visit_id)}
                >
                  {pendingKey === getActionKey("approve", selectedVisit.visit_id)
                    ? "Approving..."
                    : "Approve Selected"}
                </button>
                <button
                  className="small-btn danger-btn"
                  type="button"
                  onClick={() => void handleAction("reject", selectedVisit.visit_id)}
                  disabled={pendingKey === getActionKey("reject", selectedVisit.visit_id)}
                >
                  {pendingKey === getActionKey("reject", selectedVisit.visit_id)
                    ? "Rejecting..."
                    : "Reject Selected"}
                </button>
              </div>
            </div>
          ) : (
            <p className="empty-card">No pending request selected.</p>
          )}

          {actionMessage ? <p className="success-banner">{actionMessage}</p> : null}
          {actionError ? <p className="error-banner action-error">{actionError}</p> : null}
        </article>
      </section>

      {widgetIds.includes("recent-activity") ? (
        <section className="panel fade-in delay-3">
          <header className="panel-header">
            <h2>Recent Activity</h2>
            <span>{recentVisitors.length} rows</span>
          </header>
          <ul className="list-stack">
            {recentVisitors.length === 0 ? (
              <li className="empty-card">No recent activity</li>
            ) : (
              recentVisitors.map((visit) => (
                <li key={`employee-recent-${visit.visit_id}`} className="list-item">
                  <div>
                    <strong>{visit.visitor_name}</strong>
                    <p>
                      {visit.status || "Unknown status"} · Badge {visit.badge_code || "-"}
                    </p>
                  </div>
                  <span>{formatDateTime(visit.checkin_time)}</span>
                </li>
              ))
            )}
          </ul>
        </section>
      ) : null}
    </DashboardShell>
  );
}
